import { useEffect, useState } from "react";
import { API, SUBSCRIPTION_PLAN_TYPES } from "../../../../common/Constants";
import useFetch from "../../../../hooks/useFetch";
import PreLoader from "../../../../core/common/preLoader";
import { getAPIHeaders, isNotEmpty } from "../../../../common/Utils";
import CommonSelectV2, { Option } from "../../../../core/common/commonSelectV2";
import CustomModal from "../../../../core/common/customModal";

interface Props {
  show: boolean;
  subscriptionId: number;
  onSuccess: () => void;
  onClose: () => void;
}

interface Subscription {
  subscriptionPlanId: number;
  subscriptionPlanName: string;
  subscriptionDescription: string;
  planType: string;
  duration: number;
  planIsActive: boolean;
  pricingInfo: {
    pricingId: number;
    price: number | undefined;
    currencyCode: string;
    promotionalPrice: number | undefined;
    pricingIsActive: boolean;
  };
  courses: Array<{
    courseId: number;
    courseCode: string;
    courseTitle: string;
    courseDescription: string;
    courseType: string;
    courseStatus: string;
    accessType: string;
  }>;
}

const ModifySubscription = ({ show, subscriptionId, onSuccess, onClose }: Props) => {
  const [subscription, setSubscription] = useState<Subscription | undefined>();
  const [commercialCourses, setCommercialCourses] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>("");
  const { isLoading, error, callFetch } = useFetch();
  const [showMsgBox, setShowMsgBox] = useState<boolean>(false);

  useEffect(() => {
    if (subscription?.planType === "FREE" || subscription?.planType === "DEFAULT") {
      setSubscription((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          pricingInfo: {
            ...prev?.pricingInfo,
            promotionalPrice: 0,
          },
        };
      });
    }
  }, [subscription?.planType]);

  useEffect(() => {
    fetchSubscription();
    fetchCommercialCourses();
  }, [subscriptionId]);

  const fetchCommercialCourses = () => {
    callFetch(
      `${API.COURSE_ALL}?isCommercial=true`,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((response) => {
      setCommercialCourses(response || []);
    }).catch((err) => {
      console.error('Error fetching commercial courses:', err);
    });
  };

  // Removed this useEffect since we handle success in the API call then() callback

  useEffect(() => {
    if (error) {
      if (error.response?.data) {
        setErrorMessage(error.response?.data);
      } else {
        setErrorMessage("Something went wrong please try again later.");
      }
    }
  }, [error]);

  const fetchSubscription = () => {
    callFetch(
      `${API.SUBSCRIPTION_PLAN}/${subscriptionId}/details?includeCourses=true`,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((response) => {
      setSubscription(response);
    }).catch((err) => {
      console.error('Error fetching subscription:', err);
    });
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (!subscription) return;

    if (!isNotEmpty(subscription?.subscriptionPlanName)) {
      setErrorMessage("Please provide subscription name");
    } else if (!isNotEmpty(subscription?.subscriptionDescription)) {
      setErrorMessage("Please provide description");
    } else if (!isNotEmpty(subscription?.planType)) {
      setErrorMessage("Please select plan type");
    } else if (!isNotEmpty(subscription?.duration)) {
      setErrorMessage("Please provide duration in months");
    } else if (!subscription?.pricingInfo?.price && subscription?.planType === 'PREMIUM') {
      setErrorMessage("Price cannot be empty");
    } else {
      setErrorMessage("");
      // Transform the data to match API payload structure
      const payload = {
        subscriptionPlanId: subscriptionId,
        name: subscription.subscriptionPlanName,
        description: subscription.subscriptionDescription,
        planType: subscription.planType,
        duration: subscription.duration,
        isActive: subscription.planIsActive,
        price: subscription.pricingInfo?.price || 0,
        currencyCode: subscription.pricingInfo?.currencyCode || 'INR',
        promotionalPrice: subscription.pricingInfo?.promotionalPrice || 0,
        courseIds: subscription.courses?.map(course => course.courseId) || []
      };

      callFetch(`${API.SUBSCRIPTION_PLAN}`, "PUT", getAPIHeaders(), null, payload).then(() => {
        setShowMsgBox(true);
        onSuccess();
      }).catch((err) => {
        console.error('Error updating subscription:', err);
      });
    }
  };

  const reset = () => {
    setSubscription(undefined);
    setErrorMessage("");
    setShowMsgBox(false);
    onClose();
  };

  const courseOptions: Option[] = commercialCourses?.map((course: any) => ({
    value: course.id.toString(),
    label: `${course.title} (${course.code})`,
  }));

  const renderForm = () => {
    if (!subscription) {
      return (
        <div className="text-center py-4">
          <PreLoader showLoader={true} />
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Subscription Name <span className="text-danger"> *</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={subscription?.subscriptionPlanName}
                onChange={(e) => {
                  setSubscription({
                    ...subscription,
                    subscriptionPlanName: e.target.value,
                  });
                }}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Plan Type <span className="text-danger"> *</span>
              </label>
              <CommonSelectV2
                options={SUBSCRIPTION_PLAN_TYPES}
                onChange={(value: string) => {
                  setSubscription({
                    ...subscription,
                    planType: value,
                  });
                }}
                value={subscription?.planType ? { value: subscription.planType, label: subscription.planType } : null}
                placeHolder="Select Plan Type"
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="mb-3">
              <label className="form-label">
                Description <span className="text-danger"> *</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={subscription?.subscriptionDescription}
                onChange={(e) => {
                  setSubscription({
                    ...subscription,
                    subscriptionDescription: e.target.value,
                  });
                }}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Duration <span className="text-muted">(in months)</span><span className="text-danger"> *</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={subscription?.duration}
                onChange={(e) => {
                  setSubscription({
                    ...subscription,
                    duration:  e.target.value ? parseInt(e.target.value) : 0,
                  });
                }}
                min="0"
                step="0.01"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Currency
              </label>
              <input
                type="text"
                className="form-control"
                value={subscription?.pricingInfo?.currencyCode}
                disabled
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Price (₹)
              </label>
              <input
                type="text"
                className="form-control"
                value={subscription?.pricingInfo?.price}
                onChange={(e) => {
                  if(e.target.value){
                    setSubscription({
                      ...subscription,
                      pricingInfo: {
                        ...subscription?.pricingInfo,
                        price: parseFloat(e.target.value) || 0,
                      },
                    });
                  } else {
                    setSubscription({
                      ...subscription,
                      pricingInfo: {
                        ...subscription?.pricingInfo,
                        price: undefined,
                      },
                    });
                  }
                }}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Promotional Price (₹)
              </label>
              <input
                disabled={subscription?.planType === 'FREE' || subscription?.planType === 'DEFAULT'}
                type="text"
                className="form-control"
                value={subscription?.pricingInfo?.promotionalPrice}
                onChange={(e) => {
                  if(e.target.value){
                    setSubscription({
                      ...subscription,
                      pricingInfo: {
                        ...subscription?.pricingInfo,
                        promotionalPrice: parseFloat(e.target.value) || 0,
                      },
                    });
                  } else {
                    setSubscription({
                      ...subscription,
                      pricingInfo: {
                        ...subscription?.pricingInfo,
                        promotionalPrice: undefined,
                      },
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="mb-3">
              <label className="form-label">
                Select Courses
              </label>
              <CommonSelectV2
                isMulti
                options={courseOptions}
                onChange={(values: string[]) => {
                  // Convert course IDs to course objects for the API
                  const selectedCourses = values?.map(v => {
                    const courseId = parseInt(v);
                    const course = commercialCourses?.find((c: any) => c.id === courseId);
                    return {
                      courseId: courseId,
                      courseCode: course?.code || '',
                      courseTitle: course?.title || '',
                      courseDescription: course?.description || '',
                      courseType: course?.type || 'ACADEMIC',
                      courseStatus: course?.status || 'ACTIVE',
                      accessType: 'FULL'
                    };
                  }) || [];
                  
                  setSubscription({
                    ...subscription,
                    courses: selectedCourses,
                  });
                }}
                values={subscription?.courses?.map(course => ({
                  value: course.courseId.toString(),
                  label: course.courseTitle || '',
                }))}
                placeHolder="Select Courses"
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={subscription?.planIsActive}
                  onChange={(e) => {
                    setSubscription({
                      ...subscription,
                      planIsActive: e.target.checked,
                    });
                  }}
                />
                <label className="form-check-label">
                  Active Subscription
                </label>
              </div>
            </div>
          </div>
        </div>

        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            <i className="ti ti-alert-triangle me-2"></i>
            {errorMessage}
          </div>
        )}

        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Updating...
              </>
            ) : (
              <>
                <i className="ti ti-check me-1"></i>
                Update Subscription
              </>
            )}
          </button>
        </div>
      </form>
    );
  };

  const renderMsgBox = () => {
    return (
      <div className="pb-0">
        <div className="d-flex pb-2 justify-content-center">
          {errorMessage ? (
            <label className="text-danger fw-medium">{errorMessage}</label>
          ) : (
            <label className="text-dark fw-medium">
              Subscription updated successfully
            </label>
          )}
        </div>
      </div>
    );
  };

  return (
    <CustomModal
      show={show}
      title="Modify Subscription"
      onClose={reset}
      size="lg"
      bodyContent={
        <div>
          <PreLoader showLoader={isLoading} />
          {showMsgBox ? renderMsgBox() : renderForm()}
        </div>
      }
    />
  );
};

export default ModifySubscription;